import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  User, 
  Mail, 
  ChevronRight, 
  FileText, 
  Share2, 
  Twitter, 
  Linkedin, 
  Facebook 
} from 'lucide-react';

// --- CONFIGURATION ---
// Ensure this matches your actual backend URL
const BACKEND_URL = 'https://justurbane.in/backend_php/public';

/**
 * AuthorArticlesPage
 * Displays a professional profile for a specific author and their articles.
 * Layout inspired by high-end editorial standards (NYT, The Atlantic).
 */
const AuthorArticlesPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  // State Management
  const [data, setData] = useState({ author: null, articles: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  // --- DATA FETCHING ---
  useEffect(() => {
    const fetchAuthorData = async () => {
      try {
        setLoading(true);
        // Added explicit error handling for network failures
        const response = await fetch(`${BACKEND_URL}/api/admin/authors/${slug}`);
        
        if (!response.ok) {
           // Distinguish between 404 and server errors
           throw new Error(response.status === 404 ? 'Author not found' : 'Failed to fetch author data');
        }
        
        const result = await response.json();
        setData(result);
      } catch (err) {
        console.error("Error fetching author:", err);
        setError(err.message);
      } finally {
        // Add a slight artificial delay for smoother UI transitions if it loads too fast
        setTimeout(() => setLoading(false), 300);
      }
    };

    if (slug) fetchAuthorData();
  }, [slug]);

  // --- UTILS ---
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short', // Changed to short for a cleaner look (e.g., "Jan 20")
      day: 'numeric'
    });
  };

  // Helper to safely get the first category or default
  const getCategory = (article) => {
    return article.category || "Editorial";
  };

  // Helper for reading time
  const getReadTime = (article) => {
    return article.reading_time || Math.ceil((article.content?.length || 1000) / 1000) || 5;
  };

  // Logic to separate the featured article from the grid
  const featuredArticle = data.articles && data.articles.length > 0 ? data.articles[0] : null;
  const remainingArticles = data.articles && data.articles.length > 1 ? data.articles.slice(1) : [];

  // --- RENDER: LOADING STATE ---
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        {/* Nav Skeleton */}
        <div className="border-b border-gray-100 h-14 bg-white"></div>
        
        {/* Header Skeleton */}
        <div className="max-w-6xl mx-auto px-6 py-12 border-b border-gray-100 flex flex-col md:flex-row gap-8 items-start">
          <div className="w-24 h-24 bg-gray-100 rounded-full animate-pulse shrink-0"></div>
          <div className="flex-1 w-full">
             <div className="h-8 w-64 bg-gray-100 rounded animate-pulse mb-4"></div>
             <div className="h-4 w-full max-w-lg bg-gray-100 rounded animate-pulse mb-2"></div>
             <div className="h-4 w-2/3 bg-gray-100 rounded animate-pulse"></div>
          </div>
        </div>

        {/* Content Skeleton */}
        <div className="max-w-6xl mx-auto px-6 py-12">
           <div className="h-64 bg-gray-100 rounded-lg animate-pulse mb-12"></div>
           <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
             {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="flex flex-col gap-3">
                    <div className="h-48 bg-gray-100 rounded animate-pulse"></div>
                    <div className="h-4 w-20 bg-gray-100 rounded animate-pulse"></div>
                    <div className="h-6 w-full bg-gray-100 rounded animate-pulse"></div>
                </div>
             ))}
           </div>
        </div>
      </div>
    );
  }

  // --- RENDER: ERROR STATE ---
  if (error || !data.author) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mb-6">
            <User size={32} />
        </div>
        <h1 className="text-3xl font-serif text-gray-900 mb-3">Author Not Found</h1>
        <p className="text-gray-500 mb-8 max-w-md mx-auto leading-relaxed">
            {error || "We couldn't locate the author profile you are looking for. It may have been moved or deleted."}
        </p>
        <button 
            onClick={() => navigate('/')} 
            className="px-6 py-3 bg-black text-white text-xs font-bold uppercase tracking-widest hover:bg-gray-800 transition-all rounded-sm"
        >
          Return Home
        </button>
      </div>
    );
  }

  // --- RENDER: MAIN CONTENT ---
  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans selection:bg-gray-100 selection:text-black">
      
      {/* --- 1. NAVIGATION BAR --- */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
            <button 
                onClick={() => navigate('/')} 
                className="group flex items-center text-[11px] font-bold uppercase tracking-widest text-gray-500 hover:text-black transition-colors"
            >
                <ArrowLeft size={14} className="mr-2 group-hover:-translate-x-1 transition-transform duration-300" /> 
                <span>Back to Home</span>
            </button>

            {/* Optional: Brand or Breadcrumb could go here */}
            <div className="hidden md:block text-[11px] font-bold uppercase tracking-widest text-gray-300">
                Columnist Profile
            </div>
        </div>
      </nav>

      {/* --- 2. AUTHOR HEADER PROFILE --- */}
      <header className="bg-white border-b border-gray-900">
        <div className="max-w-7xl mx-auto px-6 py-12 md:py-16">
          <motion.div 
            initial={{ opacity: 0, y: 10 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col md:flex-row md:items-start gap-8 lg:gap-12"
          >
            {/* Avatar Section */}
            <div className="flex-shrink-0 group">
                <div className="relative">
                    {data.author.avatar ? (
                        <img 
                            src={data.author.avatar} 
                            alt={data.author.name} 
                            className="w-24 h-24 md:w-32 md:h-32 lg:w-40 lg:h-40 rounded-full object-cover border border-gray-200 shadow-sm group-hover:shadow-md transition-all duration-500" 
                        />
                    ) : (
                        <div className="w-24 h-24 md:w-32 md:h-32 lg:w-40 lg:h-40 rounded-full bg-gray-50 flex items-center justify-center border border-gray-200">
                            <User size={40} className="text-gray-300" />
                        </div>
                    )}
                    {/* Active Indicator (Mock) */}
                    <div className="absolute bottom-1 right-1 md:bottom-2 md:right-2 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></div>
                </div>
            </div>

            {/* Info Section */}
            <div className="flex-grow pt-2">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
                    <div>
                        <h4 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Columnist</h4>
                        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight leading-none">
                            {data.author.display_name || data.author.name}
                        </h1>
                    </div>
                    
                    {/* Social / Contact Actions */}
                    <div className="flex items-center gap-4">
                         {data.author.email && (
                            <a href={`mailto:${data.author.email}`} className="p-2 text-gray-400 hover:text-black hover:bg-gray-100 rounded-full transition-colors" title="Email Author">
                                <Mail size={18} />
                            </a>
                         )}
                         {/* Mock Social Icons for layout completeness */}
                        
                    </div>
                </div>

                {data.author.bio && (
                    <p className="text-gray-600 text-base md:text-lg leading-relaxed max-w-2xl font-light mb-8 border-l-2 border-gray-100 pl-4">
                        {data.author.bio}
                    </p>
                )}
                
                {/* Stats Row */}
                <div className="flex flex-wrap items-center gap-6 md:gap-10 text-[11px] font-bold uppercase tracking-widest text-gray-500">
                     <span className="flex items-center group cursor-default">
                        <FileText size={14} className="mr-2 text-gray-900 group-hover:text-blue-600 transition-colors" />
                        <span className="text-gray-900 mr-1">{data.articles ? data.articles.length : 0}</span> Published Stories
                     </span>
                     <span className="flex items-center group cursor-default">
                        <User size={14} className="mr-2 text-gray-900" />
                        <span>Member since {new Date().getFullYear()}</span>
                     </span>
                </div>
            </div>
          </motion.div>
        </div>
      </header>

      {/* --- 3. ARTICLES CONTENT AREA --- */}
      <div className="max-w-7xl mx-auto px-6 py-12 lg:py-16">
        
        {/* EMPTY STATE */}
        {(!data.articles || data.articles.length === 0) && (
          <div className="py-32 text-center border border-dashed border-gray-200 rounded-lg bg-gray-50/50">
            <div className="inline-block p-4 rounded-full bg-white mb-4 shadow-sm">
                <FileText size={24} className="text-gray-300" />
            </div>
            <p className="text-gray-500 font-serif text-lg italic">No articles published yet.</p>
          </div>
        )}

        {/* --- SECTION A: FEATURED STORY (Redesigned - Compact) --- */}
        {featuredArticle && (
             <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="mb-20 pb-16 border-b border-gray-200"
             >
                <div className="flex items-center justify-between mb-8">
                    <span className="flex items-center text-[10px] font-bold uppercase tracking-[0.2em] text-red-600">
                        <span className="w-1.5 h-1.5 bg-red-600 rounded-full mr-2"></span>
                        Latest Feature
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                        {formatDate(featuredArticle.published_at || featuredArticle.created_at)}
                    </span>
                </div>

                {/* LAYOUT CHANGE: 
                    Instead of 50/50, we use a 12-column grid.
                    Image takes 7 columns, Text takes 5 columns.
                    This makes the image dominant but not full-width, and text compact.
                */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
                    
                    {/* Image Area (7 Columns) */}
                    <div className="lg:col-span-7 order-1 relative group cursor-pointer overflow-hidden rounded-sm">
                         <Link to={`/article/${featuredArticle.slug}`} className="block relative aspect-[16/9] lg:aspect-[2/1] overflow-hidden bg-gray-100">
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-500 z-10"></div>
                            <img 
                                src={featuredArticle.hero_image || featuredArticle.heroImage || "/placeholder.jpg"} 
                                alt={featuredArticle.title}
                                onLoad={() => setImageLoaded(true)}
                                onError={(e) => (e.target.src = "/placeholder.jpg")}
                                className={`w-full h-full object-cover transform transition-transform duration-1000 ease-out group-hover:scale-105 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                            />
                         </Link>
                    </div>

                    {/* Text Area (5 Columns) - Tighter and more professional */}
                    <div className="lg:col-span-5 order-2 flex flex-col justify-center h-full pt-2">
                        <Link to={`/article/${featuredArticle.slug}`} className="group/title block">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-blue-600 mb-3">
                                {getCategory(featuredArticle)}
                            </div>
                            
                            {/* Smaller, tighter headline than before */}
                            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold leading-tight mb-4 text-gray-900 group-hover/title:text-gray-600 transition-colors">
                                {featuredArticle.title}
                            </h2>
                            
                            <p className="text-gray-600 text-base leading-relaxed mb-6 font-light line-clamp-3 md:line-clamp-4">
                                {featuredArticle.summary || featuredArticle.subtitle || "Click to read the full story detailing the events and insights captured in this featured piece."}
                            </p>
                        </Link>
                        
                        <div className="flex items-center text-[10px] font-bold uppercase tracking-widest text-gray-400 mt-auto pt-6 border-t border-gray-100">
                            <span className="flex items-center mr-4">
                                <Clock size={12} className="mr-1.5" />
                                {getReadTime(featuredArticle)} min read
                            </span>
                            <Link to={`/article/${featuredArticle.slug}`} className="ml-auto text-black flex items-center hover:underline">
                                Read Full Story <ChevronRight size={12} className="ml-1" />
                            </Link>
                        </div>
                    </div>
                </div>
             </motion.div>
        )}

        {/* --- SECTION B: REMAINING ARTICLES GRID --- */}
        {remainingArticles.length > 0 && (
            <div>
                {/* Section Title */}
                <div className="flex items-end justify-between mb-10">
                    <h3 className="text-xl font-serif font-bold text-gray-900 flex items-center">
                        Recent Stories
                    </h3>
                    <div className="hidden md:block h-px bg-gray-200 flex-grow ml-6"></div>
                </div>

                {/* Grid Layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                    {remainingArticles.map((article, index) => (
                        <motion.article 
                            key={article.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ delay: index * 0.05, duration: 0.5 }}
                            className="group flex flex-col h-full border-b border-gray-100 pb-8 md:border-none md:pb-0"
                        >
                            {/* Thumbnail */}
                            <Link to={`/article/${article.slug}`} className="block overflow-hidden rounded-sm mb-5 aspect-[3/2] bg-gray-100 relative">
                                <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-2 py-1 text-[9px] font-bold uppercase tracking-widest z-10">
                                    {getCategory(article)}
                                </div>
                                <img 
                                    src={article.hero_image || article.heroImage || "/placeholder.jpg"} 
                                    alt={article.title}
                                    onError={(e) => (e.target.src = "/placeholder.jpg")}
                                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                                />
                            </Link>
                            
                            {/* Article Info */}
                            <div className="flex flex-col flex-grow">
                                <div className="flex items-center text-[10px] text-gray-400 mb-3 space-x-2">
                                     <Calendar size={10} />
                                     <span className="font-bold uppercase tracking-widest">
                                        {formatDate(article.published_at || article.created_at)}
                                     </span>
                                </div>

                                <h2 className="font-serif text-xl font-bold leading-snug mb-3 text-gray-900 group-hover:text-blue-700 transition-colors">
                                    <Link to={`/article/${article.slug}`}>
                                        {article.title}
                                    </Link>
                                </h2>

                                <p className="text-gray-500 text-sm line-clamp-3 mb-4 leading-relaxed font-light">
                                    {article.summary || article.subtitle || "A brief preview of the article content goes here to entice the reader."}
                                </p>

                                {/* Footer Info */}
                                <div className="mt-auto pt-4 flex items-center justify-between">
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 flex items-center">
                                        <Clock size={10} className="mr-1.5" />
                                        {getReadTime(article)} MIN READ
                                    </span>
                                    
                                    <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <button className="text-gray-400 hover:text-black" title="Share">
                                            <Share2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </motion.article>
                    ))}
                </div>
                
                {/* Bottom decorative element */}
                <div className="mt-20 border-t border-gray-100 pt-8 text-center">
                    <p className="text-[10px] uppercase tracking-widest text-gray-400">End of list</p>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default AuthorArticlesPage;